import { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import debounce from 'lodash.debounce'
import { usePathname, useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'

import {
  CustomerTypes,
  extractCustomerFromClientSession
} from 'modules/customer'
import {
  countJumpCalculation,
  helperFloat,
  ProductTypes
} from 'modules/products'
import { SeoListName } from 'shared/analytics'
import { noop } from 'shared/utils/common'
import { isNilOrEmpty } from 'shared/utils/ramda'
import { createAsPath } from 'shared/utils/searchParams'

import { addToCartSeo, removeFromCartSeo } from '../../../analytics'
import useCartStore from '../../../store/cartStore'
import {
  CartReplacementsPayload,
  CartReplacementsPayloadItem
} from '../../../types'
import { IconChangeQuantity } from '../../items/components/AddToCartButton/IconChangeQuantity'

import styles from './ReplaceCartItemButton.module.scss'

const REPLACEMENTS_LIST_NAME = SeoListName.REPLACEMENTS_LIST_NAME

type ReplaceCartItemButtonProps = {
  quantityJump?: number
  substitute: ProductTypes
  realProductId?: string
  idxOnList: number
}

const ReplaceCartItemButton = (props: ReplaceCartItemButtonProps) => {
  const { quantityJump, substitute, realProductId, idxOnList } = props
  const session = useSession()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const asPath = createAsPath(pathname, searchParams) // @sieradz - for useEffect, on path change detection and refresh view button add to cart
  const precision = quantityJump || 1
  const quantityLimit = Number(substitute.maxQuantityPerSaleOrder) || 999999999
  const [productQuantity, setProductQuantity] = useState(0)
  const user: CustomerTypes | undefined = extractCustomerFromClientSession(session)

  const {
    cart,
    isLoading,
    getProductCount,
    getProductReplacements,
    setProductReplacements
  } = useCartStore()
  const replacements = getProductReplacements()
  const incrementButtonRef = useRef<HTMLButtonElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  const handleAddToCartSeo = (quantity: number, type: 'add' | 'increment') => {
    addToCartSeo({
      user,
      product: substitute,
      button_place: 'Zamienniki wybrane dla Ciebie',
      idxOnList: idxOnList || 0,
      productQuantity: quantity,
      cart,
      listName: REPLACEMENTS_LIST_NAME,
      type
    })
  }

  const handleRemoveFromCartSeo = (
    quantity: number,
    type: 'decrement' | 'remove'
  ) => {
    if (type === 'decrement') {
      return
    }

    removeFromCartSeo({
      user,
      product: substitute,
      button_place: 'Zamienniki wybrane dla Ciebie',
      idxOnList: idxOnList || 0,
      productQuantity: productQuantity - quantity,
      cart,
      listName: REPLACEMENTS_LIST_NAME,
      type
    })
  }

  useEffect(() => {
    const productCount = getProductCount(substitute.id)

    setProductQuantity(productCount)
  }, [cart, asPath])

  const handleUpdateReplacement = useCallback(
    debounce(
      (
        replacementsArray: CartReplacementsPayload[],
        prodId?: string,
        subId?: string,
        quantity?: number
      ) => {
        const payload = replacementsArray?.map(
          (replacement: CartReplacementsPayload) => {
            if (replacement.productId === prodId) {
              const subs = replacement?.substitutes || []

              if (isNilOrEmpty(subs?.find(s => s.substituteId === subId))) {
                subs.push({
                  substituteId: subId || '',
                  quantity: quantity || 1
                })
              }

              return {
                ...replacement,
                substitutes: subs
                  ?.map((subItem: CartReplacementsPayloadItem) => {
                    if (subItem.substituteId === subId) {
                      return {
                        ...subItem,
                        quantity: quantity || 0
                      }
                    } else {
                      return subItem
                    }
                  })
                  ?.filter(s => s.quantity !== 0)
              }
            } else {
              return replacement
            }
          }
        )

        setProductReplacements(payload)
      },
      500
    ),
    [session]
  )

  const backFocusToElement = () => {
    // button/ input is optional rendered. After change we don't want to lost focun on
    setTimeout(() => {
      if (
        incrementButtonRef.current &&
        document.contains(incrementButtonRef.current)
      ) {
        incrementButtonRef.current?.focus()
      } else if (buttonRef.current && document.contains(buttonRef.current)) {
        buttonRef.current?.focus()
      }
    }, 10)
  }

  const addNewItem = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()

    const quantity = quantityJump || helperFloat('1')

    handleUpdateReplacement(
      replacements,
      realProductId,
      substitute.id,
      quantity
    )
    backFocusToElement()
    setProductQuantity(quantity)
    handleAddToCartSeo(quantity, 'add')
  }

  const handleIncrement = () => {
    handleUpdateReplacement.cancel()
    const quantity = countJumpCalculation(
      'increment',
      productQuantity,
      precision,
      quantityLimit
    )

    handleUpdateReplacement(
      replacements,
      realProductId,
      substitute.id,
      quantity
    )

    setProductQuantity(quantity)
    handleAddToCartSeo(quantity, 'increment')
  }

  const handleDecrement = () => {
    handleUpdateReplacement.cancel()
    const quantity = countJumpCalculation(
      'decrement',
      productQuantity,
      precision,
      0
    )

    handleUpdateReplacement(
      replacements,
      realProductId,
      substitute.id,
      quantity
    )

    setProductQuantity(quantity)

    if (quantity === 0) {
      backFocusToElement()
    }
    handleRemoveFromCartSeo(quantity, 'decrement')
  }

  const displayButtonContent = () => {
    return productQuantity ? (
      <div className={clsx(styles.base, styles.withQuantity)}>
        <IconChangeQuantity
          className='add_to_cart_decrement_button'
          role='button'
          onClick={handleDecrement}
          isLoading={isLoading}
          type='minus'
          disabled={false}
        />

        {productQuantity}

        <IconChangeQuantity
          className='add_to_cart_increment_button'
          role='button'
          onClick={handleIncrement}
          isLoading={isLoading}
          type='add'
          disabled={false}
          ref={incrementButtonRef}
        />
      </div>
    ) : (
      <button
        className={clsx(styles.base, styles.button)}
        id='replacement_replace_button'
        onClick={isLoading ? noop : addNewItem}
        ref={buttonRef}
      >
        Zamień
      </button>
    )
  }

  return <div className={styles.wrapper}>{displayButtonContent()}</div>
}

export default ReplaceCartItemButton
